import React from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useColumnManager } from "@/lib/column-manager";
import type { SimpleColumn } from "@/lib/column-manager";
import { useViewMode } from "../hooks";
import { reorderVisibleColumnOrder } from "../utils/reorder";
import { ColumnActions } from "./ColumnActions";
import { ColumnCategory } from "./ColumnCategory";
import { ViewModeToggle } from "./ViewModeToggle";
import { ColumnSummary } from "./ColumnSummary";
import { ShownColumnsList } from "./ShownColumnsList";

interface ColumnManagerPanelProps {
  /** Close the surrounding modal (wired to the footer "Done" button). */
  onDone: () => void;
}

/**
 * Inner content of the column manager. Two panes:
 *  - LEFT ("Available"): every column, grouped by category and collapsible, with
 *    a search box — progressive disclosure so users drill only into the group
 *    they care about (metadata, dates, …). Checkboxes show/hide.
 *  - RIGHT ("Shown"): the visible columns as an ordered, drag-to-reorder list.
 *    Order here is the table's left-to-right column order.
 *
 * Extracted from the modal shell (index.tsx) so it can be unit-tested against a
 * mock ColumnManager context without the global modal stack.
 */
export function ColumnManagerPanel({ onDone }: ColumnManagerPanelProps) {
  const {
    columns,
    visibleColumns,
    columnOrder,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    resetToDefaults,
    toggleCategory,
    getCategoryStats,
    setColumnOrder,
  } = useColumnManager();

  const { viewMode, setViewMode } = useViewMode();

  const [search, setSearch] = React.useState("");
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(
    new Set(["basic", "status", "assignment"])
  );

  const query = search.trim().toLowerCase();
  const isSearching = query.length > 0;

  const toggleCategoryExpansion = React.useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  // Group columns by category, applying the search filter. Categories with no
  // matching column are dropped while searching.
  const groupedColumns = React.useMemo(() => {
    const groups: Record<string, SimpleColumn[]> = {};
    for (const col of columns) {
      if (isSearching && !col.label.toLowerCase().includes(query)) continue;
      (groups[col.category] ||= []).push(col);
    }
    return groups;
  }, [columns, isSearching, query]);

  const columnsById = React.useMemo(() => {
    const map = new Map<string, SimpleColumn>();
    for (const col of columns) map.set(col.id, col);
    return map;
  }, [columns]);

  // Visible columns in display (columnOrder) sequence — what the Shown pane renders.
  const shownColumns = React.useMemo(() => {
    const seen = new Set<string>();
    const ordered: SimpleColumn[] = [];
    for (const id of columnOrder) {
      const col = columnsById.get(id);
      if (col?.visible) {
        ordered.push(col);
        seen.add(id);
      }
    }
    // Drift guard: surface any visible column missing from columnOrder.
    for (const col of visibleColumns) {
      if (!seen.has(col.id)) ordered.push(col);
    }
    return ordered;
  }, [columnOrder, columnsById, visibleColumns]);

  const handleReorder = React.useCallback(
    (reorderedVisibleIds: string[]) => {
      setColumnOrder(reorderVisibleColumnOrder(columnOrder, reorderedVisibleIds));
    },
    [columnOrder, setColumnOrder]
  );

  const groupedEntries = Object.entries(groupedColumns);

  return (
    <div className="flex flex-col gap-4 bg-card flex-1 min-h-0">
      {/* View mode */}
      <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />

      <div className="border-t" />

      {/* Two-pane arranger */}
      <div className="flex flex-1 min-h-0 flex-col gap-4 md:flex-row">
        {/* LEFT — available columns, grouped + searchable */}
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-muted-foreground">Available columns</span>
          </div>

          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search columns…"
              aria-label="Search columns"
              className="pl-8"
            />
          </div>

          <ColumnActions
            onShowAll={showAllColumns}
            onHideAll={hideAllColumns}
            onReset={resetToDefaults}
          />

          <div className="flex-1 space-y-2 overflow-y-auto">
            {groupedEntries.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                No columns match “{search}”.
              </div>
            ) : (
              groupedEntries.map(([categoryKey, categoryColumns]) => (
                <ColumnCategory
                  key={categoryKey}
                  categoryKey={categoryKey}
                  categoryColumns={categoryColumns}
                  isExpanded={isSearching || expandedCategories.has(categoryKey)}
                  onToggleExpansion={toggleCategoryExpansion}
                  onToggleCategory={() => toggleCategory(categoryKey)}
                  onToggleColumn={toggleColumn}
                  getCategoryStats={getCategoryStats}
                />
              ))
            )}
          </div>
        </div>

        {/* vertical divider on desktop */}
        <div className="hidden border-l md:block" />

        {/* RIGHT — shown columns, ordered + draggable */}
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Shown columns ({shownColumns.length})
            </span>
            <span className="text-xs text-muted-foreground">Drag to reorder</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ShownColumnsList
              shownColumns={shownColumns}
              onReorder={handleReorder}
              onRemove={toggleColumn}
            />
          </div>
        </div>
      </div>

      <ColumnSummary
        visibleCount={visibleColumns.length}
        totalCount={columns.length}
        onDone={onDone}
      />
    </div>
  );
}
